import { useCallback, useEffect, useRef, useState } from "react";

// Draft persistence for the Brand Publish wizard.
//  - JSON-serialisable state  -> localStorage (small, synchronous read on mount)
//  - File/Blob values (local videos, custom thumbnails) -> IndexedDB, since
//    localStorage can only hold strings. They are swapped for placeholders in
//    the JSON and rehydrated on restore.
// Nothing is restored silently: the caller shows a "Resume draft?" prompt.

const DRAFT_KEY = "cv:brand-publish-draft:v1";
const DRAFT_TTL_MS = 7 * 24 * 60 * 60 * 1000;
const DB_NAME = "cv-brand-publish-draft";
const STORE = "files";
const SAVE_DEBOUNCE_MS = 600;
// Blob URLs die with the page, so they are dropped and rebuilt on restore.
const DERIVED_KEYS = new Set(["thumbnailPreview"]);
// Large values kept in IndexedDB beside the files: every edited card's editor
// document would otherwise push the JSON past localStorage's ~5 MB quota, and
// a failed write there loses the whole draft.
const IDB_KEYS = new Set(["renderPayload"]);

const isBlobLike = (v) =>
  (typeof File !== "undefined" && v instanceof File) ||
  (typeof Blob !== "undefined" && v instanceof Blob);

function openDb() {
  return new Promise((resolve, reject) => {
    const req = indexedDB.open(DB_NAME, 1);
    req.onupgradeneeded = () => req.result.createObjectStore(STORE);
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
}

const txDone = (tx) =>
  new Promise((resolve, reject) => {
    tx.oncomplete = () => resolve();
    tx.onerror = () => reject(tx.error);
    tx.onabort = () => reject(tx.error);
  });

async function replaceFiles(files) {
  const db = await openDb();
  const tx = db.transaction(STORE, "readwrite");
  const store = tx.objectStore(STORE);
  store.clear();
  Object.entries(files).forEach(([id, file]) => store.put(file, id));
  await txDone(tx);
  db.close();
}

async function readFiles(ids) {
  const db = await openDb();
  const store = db.transaction(STORE, "readonly").objectStore(STORE);
  const out = {};
  await Promise.all(
    ids.map(
      (id) =>
        new Promise((resolve) => {
          const req = store.get(id);
          req.onsuccess = () => {
            out[id] = req.result || null;
            resolve();
          };
          req.onerror = () => resolve();
        })
    )
  );
  db.close();
  return out;
}

async function clearFiles() {
  try {
    const db = await openDb();
    const tx = db.transaction(STORE, "readwrite");
    tx.objectStore(STORE).clear();
    await txDone(tx);
    db.close();
  } catch (_) {
    // non-fatal
  }
}

function encode(value, files) {
  if (isBlobLike(value)) {
    const id = `f${Object.keys(files).length}`;
    files[id] = value;
    return { __file: id, name: value.name || "", type: value.type || "" };
  }
  if (Array.isArray(value)) return value.map((v) => encode(v, files));
  if (value && typeof value === "object") {
    const out = {};
    Object.keys(value).forEach((k) => {
      if (DERIVED_KEYS.has(k)) return;
      if (IDB_KEYS.has(k) && value[k] && typeof value[k] === "object") {
        const id = `f${Object.keys(files).length}`;
        files[id] = value[k];
        out[k] = { __file: id };
        return;
      }
      out[k] = encode(value[k], files);
    });
    return out;
  }
  return value;
}

function decode(value, files) {
  if (Array.isArray(value)) return value.map((v) => decode(v, files));
  if (value && typeof value === "object") {
    if (value.__file) return files[value.__file] || null;
    const out = {};
    Object.keys(value).forEach((k) => {
      out[k] = decode(value[k], files);
    });
    // A file that could not be recovered must not leave the step looking
    // resolved — otherwise submit would skip the upload for it.
    if (typeof out.localVideoPath === "string" && "localFile" in out && !out.localFile) {
      delete out.localVideoPath;
      delete out.localFile;
    }
    if (out.thumbnailSource === "custom" && "thumbnailFile" in out && !out.thumbnailFile) {
      out.thumbnailSource = "none";
    }
    if (out.thumbnailFile instanceof Blob) {
      out.thumbnailPreview = URL.createObjectURL(out.thumbnailFile);
    }
    return out;
  }
  return value;
}

function collectFileIds(value, ids = []) {
  if (Array.isArray(value)) value.forEach((v) => collectFileIds(v, ids));
  else if (value && typeof value === "object") {
    if (value.__file) ids.push(value.__file);
    else Object.values(value).forEach((v) => collectFileIds(v, ids));
  }
  return ids;
}

function readEnvelope() {
  try {
    const raw = localStorage.getItem(DRAFT_KEY);
    if (!raw) return null;
    const env = JSON.parse(raw);
    if (!env || env.version !== 1 || Date.now() - env.savedAt > DRAFT_TTL_MS) {
      localStorage.removeItem(DRAFT_KEY);
      return null;
    }
    return env;
  } catch (_) {
    return null;
  }
}

/**
 * @param {object}  state    serialisable wizard state (may contain File values)
 * @param {number}  step     active wizard step
 * @param {boolean} hasData  true once the user has entered anything worth saving
 * @returns {{ draft, restore, discard, clear, blocked }}
 *   draft   – envelope info ({ savedAt, step }) if a resumable draft exists
 *   restore – async, resolves { state, step } with Files rehydrated
 *   discard – drop the stored draft and start fresh
 *   clear   – drop the draft after a successful publish
 */
export default function useWizardDraft(state, step, hasData) {
  const [draft, setDraft] = useState(() => {
    const env = readEnvelope();
    return env ? { savedAt: env.savedAt, step: env.step } : null;
  });
  // Autosave stays off until the user resolves the prompt, so an empty fresh
  // wizard can't overwrite the draft they are about to resume.
  const [saving, setSaving] = useState(() => !readEnvelope());
  const latest = useRef({ state, step });
  latest.current = { state, step };

  useEffect(() => {
    if (!saving || !hasData) return undefined;
    const t = setTimeout(async () => {
      try {
        const files = {};
        const encoded = encode(latest.current.state, files);
        localStorage.setItem(
          DRAFT_KEY,
          JSON.stringify({ version: 1, savedAt: Date.now(), step: latest.current.step, state: encoded })
        );
        await replaceFiles(files);
      } catch (_) {
        // Quota or private-mode failure: the wizard keeps working, just unsaved.
      }
    }, SAVE_DEBOUNCE_MS);
    return () => clearTimeout(t);
  }, [state, step, saving, hasData]);

  const restore = useCallback(async () => {
    const env = readEnvelope();
    if (!env) return null;
    let files = {};
    try {
      files = await readFiles(collectFileIds(env.state));
    } catch (_) {
      // files stay missing; decode() un-resolves the affected steps
    }
    setDraft(null);
    setSaving(true);
    return { state: decode(env.state, files), step: env.step };
  }, []);

  const clear = useCallback(() => {
    setSaving(false);
    try {
      localStorage.removeItem(DRAFT_KEY);
    } catch (_) {
      // non-fatal
    }
    return clearFiles();
  }, []);

  const discard = useCallback(() => {
    setDraft(null);
    clear().then(() => setSaving(true));
  }, [clear]);

  return { draft, restore, discard, clear };
}
